import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import jobsApi from '../../../APIs/jobs';
import { ProgressStep, Typography } from '../../../designComponents';
import { RootState } from '../../../store/selectors';
import { requestAPI } from '../../../utils';
import { getReviewHistory } from '../../../utils/reviews';
import InProgress from './InProgress';
import ReviewedByEscPlan from './ReviewedByEscPlan';
import ReviewHistory from './ReviewHistory';
import SentForReview from './SentForReview';
import SentToClient from './SentToClient';
import { ReviewAndSendContainerStyled } from './style';
import UploadAndSendToReview from './UploadAndSendToReview';

type jobStatus =
  | 'uploadAndSendToReview'
  | 'sentForReview'
  | 'reviewedByEscPlan'
  | 'sentToClient'
  | 'inProcess';

const jobProcessStep = [
  'INPROGRESS',
  'PDF_UPLOAD',
  'REVIEW',
  'SEND_CLIENT',
  'COMPLETED',
] as const;

type ProcessStepType = (typeof jobProcessStep)[number];

const statusStepMapping: Record<ProcessStepType, jobStatus> = {
  INPROGRESS: 'inProcess',
  PDF_UPLOAD: 'uploadAndSendToReview',
  REVIEW: 'sentForReview',
  SEND_CLIENT: 'reviewedByEscPlan',
  COMPLETED: 'sentToClient',
};
type Props = {
  jobAndProposalId: string;
};

const ReviewAndSend = ({ jobAndProposalId }: Props) => {
  const [inProgress, setInProgress] = useState<number>(1);
  const [completedStep, setCompletedStep] = useState<number>(1);
  const [jobStatus, setJobStatus] = useState<jobStatus>('inProcess');
  const jobProcess = useSelector((state: RootState) => state.singleJobProcess);

  const reviews = jobProcess?.review || [];
  const clientReviews = jobProcess?.clientReview || [];

  const historyReview = getReviewHistory(reviews);

  const handleProceedForward = () => {
    setJobStatus('uploadAndSendToReview');
  };

  const getJ0bProcess = async (id: string) => {
    try {
      await requestAPI(jobsApi.getJobProcessByJobProposal(id));
    } catch (err) {
      console.error('Error on fetching own uploaded data , ', err);
    }
  };

  useEffect(() => {
    if (jobAndProposalId) {
      getJ0bProcess(jobAndProposalId);
    }
  }, [jobAndProposalId]);

  useEffect(() => {
    if (jobProcess?._id) {
      const completedStepNumber = jobProcessStep.findIndex(
        (x) => x === jobProcess.status
      );
      setInProgress(completedStepNumber + 1);
      const isProcessCompleted = jobProcess.status === 'COMPLETED';
      setCompletedStep(
        isProcessCompleted ? completedStepNumber + 1 : completedStepNumber
      );

      if (jobProcess.status) {
        const jobProcessStatus = jobProcess.status as ProcessStepType;
        setJobStatus(statusStepMapping[jobProcessStatus]);
      }
    }
  }, [jobProcess]);

  return (
    <ReviewAndSendContainerStyled>
      <Typography variant="title" strong style={{ marginBottom: '10px' }}>
        Upload Complete PDF
      </Typography>
      <div style={{ marginBottom: '30px' }}>
        <ProgressStep
          completedStep={completedStep}
          inProgressStep={inProgress}
          steps={[
            {
              label: 'In Progress',
              stepNumber: 1,
            },
            {
              label: 'PDF Upload',
              stepNumber: 2,
            },
            {
              label: 'Review by Escape Plan',
              stepNumber: 3,
            },
            {
              label: 'Sent to Client',
              stepNumber: 4,
            },
            {
              label: 'Completed',
              stepNumber: 5,
            },
          ]}
        />
      </div>
      {
        {
          inProcess: (
            <InProgress
              jobAndProposalId={jobAndProposalId}
              OnProceedForward={handleProceedForward}
            />
          ),
          uploadAndSendToReview: (
            <UploadAndSendToReview jobAndProposalId={jobAndProposalId} />
          ),
          sentForReview: <SentForReview />,
          reviewedByEscPlan: (
            <ReviewedByEscPlan jobAndProposalId={jobAndProposalId} />
          ),
          sentToClient: <SentToClient jobAndProposalId={jobAndProposalId} />,
        }[jobStatus]
      }
      <ReviewHistory
        historyReview={historyReview}
        clientReview={clientReviews}
      />
    </ReviewAndSendContainerStyled>
  );
};

export default ReviewAndSend;
